// IconButton component

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'

import IntlMessage from '../intl/intl_message.jsx'
import IntlUtil from '../../../lib/intlutil.jsx'
import SvgIcon from '../svgicon/svgicon.jsx'

class IconButton extends Component {
  static get propTypes () {
    return {
      id: PropTypes.string,
      title: PropTypes.string,
      icon: PropTypes.string,
      tooltip: PropTypes.string,
      tooltipPosition: PropTypes.oneOf(['right', 'left', 'bottom', 'top']),
      tooltipData: PropTypes.object,
      size: PropTypes.oneOf(['normal', 'big', 'small', 'mobile']),
      type: PropTypes.oneOf([
        'normal',
        'primary',
        'error',
        'transparent',
        'secondary',
        'default'
      ]),
      onClick: PropTypes.func,
      canHover: PropTypes.bool,
      animation: PropTypes.string,
      prefix: PropTypes.string,
      suffix: PropTypes.string,
      className: PropTypes.string,
      multilingual: PropTypes.bool,
      children: PropTypes.any,
      disabled: PropTypes.bool,
      href: PropTypes.string,
      stats: PropTypes.array,
      isSvg: PropTypes.bool,
      svgSize: PropTypes.string
    }
  }

  static get defaultProps () {
    return {
      id: '',
      title: '',
      icon: '',
      tooltip: '',
      tooltipPosition: 'right',
      tooltipData: {},
      size: 'normal',
      type: 'normal',
      onClick: null,
      canHover: true,
      animation: '',
      prefix: '',
      suffix: '',
      className: '',
      multilingual: true,
      children: null,
      disabled: false,
      href: '',
      stats: [],
      isSvg: false,
      svgSize: '16'
    }
  }

  constructor (props) {
    super(props)

    this.state = {
      tooltipStyle: null,
      tipClassName: ''
    }
    this._references = {
      button: React.createRef(),
      tooltip: React.createRef()
    }
  }

  render () {
    const {
      canHover,
      icon,
      tooltip,
      prefix,
      suffix,
      type,
      className,
      tooltipPosition,
      tooltipData,
      size,
      multilingual,
      disabled,
      href,
      stats,
      isSvg,
      svgSize,
      id,
      ...others
    } = this.props

    const iconClass = `icon icon-${icon} normal`
    const iconHvrClass = `icon icon-${icon}-hover hover`
    const typeClass = type ? `icon-button_${type}` : ''

    let tooltipPositionClass = ''
    if (tooltipPosition === 'left') {
      tooltipPositionClass = 'tipleft'
    } else if (tooltipPosition === 'right') {
      tooltipPositionClass = 'tipright'
    } else if (tooltipPosition === 'top') {
      tooltipPositionClass = 'tiptop'
    } else if (tooltipPosition === 'bottom') {
      tooltipPositionClass = 'tipbottom'
    }

    let sizeClass = ''
    if (size === 'big') {
      sizeClass = 'big'
    } else if (size === 'small') {
      sizeClass = 'small'
    } else if (size === 'mobile') {
      sizeClass = 'mobile'
    }

    const _class = classnames(
      'icon-button',
      {
        'icon-button_hashover': canHover,
        disabled: disabled
      },
      className,
      typeClass,
      sizeClass,
      tooltipPositionClass
    )
    const tooltipStyle = this.state.tooltipStyle
    const statsTooltipStyle = _.cloneDeep(this.state.tooltipStyle)
    const tipClassName = this.state.tipClassName
    const titleConvert = this.props.title
      ? IntlUtil.convertMessage(this.props.title)
      : ''
    const element = href ? 'a' : 'div'
    let elementProps = {
      ref: this._references.button,
      className: _class,
      title: titleConvert,
      onMouseEnter: this._onMouseEnter.bind(this)
    }
    if (id) {
      elementProps.id = id
    }

    if (href) {
      elementProps = {
        ...others,
        ref: this._references.button,
        href,
        className: _class,
        title: titleConvert,
        onMouseEnter: this._onMouseEnter.bind(this)
      }
    }
    if (statsTooltipStyle) {
      statsTooltipStyle.top = '-5px'
    }

    return React.createElement(
      element,
      elementProps,
      prefix ? (
        <span className='prefix'>
          <IntlMessage ident={prefix} />
        </span>
      ) : null,
      tooltip && stats.length === 0 ? (
        <span
          ref={this._references.tooltip}
          className='tooltip'
          style={tooltipStyle}
        >
          {multilingual ? (
            <IntlMessage ident={tooltip} value={tooltipData} />
          ) : (
            tooltip
          )}
          <span
            className={`tip${tipClassName ? ' ' + tipClassName : ''}`}
          />
          <span className={`tipicon${tipClassName ? ' ' + tipClassName : ''}`}>
            <i className='icon icon-common-infotip' />
          </span>
        </span>
      ) : null,
      !tooltip && stats.length > 0 ? (
        <div
          ref={this._references.tooltip}
          className='tooltip stattooltip'
          style={statsTooltipStyle}
        >
          {this._renderStatInfos()}
          <span className='tooltiparrow' />
          <span className='hoverblk' />
        </div>
      ) : null,
      <span className='cont' onClick={href ? null : this._onClick.bind(this)}>
        {!isSvg && icon && <i className={iconClass} />}
        {!isSvg && canHover ? <i className={iconHvrClass} /> : ''}
        {isSvg && <SvgIcon name={icon} size={svgSize} className='icon' />}
        {this.props.children}
      </span>,
      suffix ? (
        <span className='suffix'>
          <IntlMessage ident={suffix} />
        </span>
      ) : null
    )
  }

  /* Event Handlers */
  _onClick (e) {
    e.preventDefault()

    if (this.props.onClick && !this.props.disabled) {
      this.props.onClick(e)
    }
  }

  _onMouseEnter () {
    this._updateTooltipStyle()
  }

  _renderStatInfos () {
    let subItmIdx = 0
    return this.props.stats.map((statItem, idx) => {
      if (!statItem.hasSubInfos) {
        const itmClss = `stattooltipitem stattooltipitem_${idx}`
        return (
          <div className={itmClss} key={idx}>
            <span className='itemlabel'>
              <IntlMessage ident={statItem.label} />
            </span>
            <span className='itemvalue'>
              {statItem.showWarning && (
                <span className='staticon'>
                  <i className='icon icon-header-warning' />
                </span>
              )}
              <span className='statval'>{statItem.value}</span>
            </span>
          </div>
        )
      } else {
        const subItmCls =
          subItmIdx === 0
            ? 'stattooltipitem hassubinfo hasborder'
            : 'stattooltipitem hassubinfo'
        subItmIdx++

        return (
          <div className={subItmCls} key={idx}>
            {statItem.subItems.map((subItem, subIdx) => {
              const itmClss = `statsubitem statsubitem_${subIdx}`
              return (
                <div className={itmClss} key={subIdx}>
                  <span className='itemlabel'>
                    <IntlMessage ident={subItem.label} />
                  </span>
                  <span className='itemvalue'>
                    <span className='statval'>
                      <span className='value'>{subItem.value}</span>
                      {subItem.seperatorText && (
                        <span className='statsymbol'>
                          {subItem.seperatorText}
                        </span>
                      )}
                    </span>
                  </span>
                </div>
              )
            })}
          </div>
        )
      }
    })
  }

  _updateTooltipStyle () {
    if (this._references.tooltip.current && this._references.button.current) {
      const tooltipPosition = this.props.tooltipPosition
      const {
        height,
        width
      } = this._references.tooltip.current.getBoundingClientRect()
      const {
        width: buttonWidth,
        height: buttonHeight,
        left: buttonLeft
      } = this._references.button.current.getBoundingClientRect()
      const { innerWidth: windowWidth } = window
      const tooltipOffsetX = buttonLeft + buttonWidth + width
      let tooltipStyle = null
      let tipClassName = ''

      if (tooltipPosition === 'left') {
        tooltipStyle = {
          right: '100%',
          left: 'auto',
          top: (buttonHeight - height) / 2
        }
      } else if (tooltipPosition === 'right') {
        tooltipStyle = {
          left: '100%',
          right: 'auto',
          top: (buttonHeight - height) / 2
        }
      } else if (tooltipPosition === 'top') {
        tooltipStyle = {
          bottom: '100%',
          top: 'auto',
          left: (buttonWidth - width) / 2
        }

        if (tooltipOffsetX > windowWidth) {
          tooltipStyle.left = 'auto'
          tooltipStyle.right = 0

          tipClassName = 'right'
        }
      } else if (tooltipPosition === 'bottom') {
        tooltipStyle = {
          top: '100%',
          bottom: 'auto',
          left: (buttonWidth - width) / 2
        }

        if (tooltipOffsetX > windowWidth) {
          tooltipStyle.left = 'auto'
          tooltipStyle.right = 0

          tipClassName = 'right'
        }
      }

      this.setState({ tooltipStyle: tooltipStyle, tipClassName })
    }
  }
}

export default IconButton
